import React from 'react';
import { Image, Pressable, Text, View, ActivityIndicator } from 'react-native';
import { ticketColors } from '../../shared/theme/ticketColors';
import { catalogProductFormStyles as styles } from './catalogProductForm.styles';
import { resolveImageUrl } from '../infra/resolveImageUrl';

type Props = {
  imageUrl?: string;
  onPickImage: () => void;
  onRemoveImage: () => void;
  uploading?: boolean;
  compact?: boolean;
  desktopRail?: boolean;
};

const IMAGE_SIZE = 96;

export function ProductImagePicker({ imageUrl, onPickImage, onRemoveImage, uploading, compact = false, desktopRail = false }: Props) {
  if (compact) {
    return (
      <View style={styles.quickImageField}>
        <Text style={styles.ticketFormLabel}>Foto</Text>
        {imageUrl ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Image
              source={{ uri: resolveImageUrl(imageUrl, 'thumb') ?? imageUrl }}
              style={styles.quickImagePreview}
              resizeMode="cover"
            />
            <View style={{ flex: 1, gap: 6 }}>
              <Pressable
                onPress={onPickImage}
                disabled={uploading}
                style={[styles.ticketFormInput, styles.quickImageButton, { opacity: uploading ? 0.6 : 1 }]}
                accessibilityRole="button"
                accessibilityLabel="Cambiar imagen"
              >
                <Text style={{ fontSize: 12, fontWeight: '600', color: ticketColors.ink }}>Cambiar…</Text>
              </Pressable>
              <Pressable
                onPress={onRemoveImage}
                disabled={uploading}
                style={[styles.ticketFormInput, styles.quickImageButton, { opacity: uploading ? 0.6 : 1 }]}
                accessibilityRole="button"
                accessibilityLabel="Quitar imagen"
              >
                <Text style={{ fontSize: 12, fontWeight: '600', color: ticketColors.ink3 }}>Quitar</Text>
              </Pressable>
            </View>
          </View>
        ) : (
          <Pressable
            onPress={onPickImage}
            disabled={uploading}
            style={[styles.ticketFormInput, styles.quickImageButton, { opacity: uploading ? 0.6 : 1 }]}
            accessibilityRole="button"
            accessibilityLabel="Agregar imagen"
            testID="catalog.form.image.pick"
          >
            {uploading ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <ActivityIndicator size="small" color={ticketColors.ink2} />
                <Text style={{ fontSize: 12, fontWeight: '600', color: ticketColors.ink3 }}>Subiendo…</Text>
              </View>
            ) : (
              <Text style={{ fontSize: 13, fontWeight: '600', color: ticketColors.ink3 }}>Agregar… +</Text>
            )}
          </Pressable>
        )}
      </View>
    );
  }

  if (desktopRail) {
    return (
      <View style={styles.ticketFormField}>
        {imageUrl ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Image
              source={{ uri: resolveImageUrl(imageUrl, 'thumb') ?? imageUrl }}
              style={styles.railImagePreview}
              resizeMode="cover"
            />
            <View style={{ flex: 1, gap: 6 }}>
              <Pressable
                onPress={onPickImage}
                disabled={uploading}
                style={[styles.ticketFormInput, { minHeight: 40, justifyContent: 'center', opacity: uploading ? 0.6 : 1 }]}
                accessibilityRole="button"
                accessibilityLabel="Cambiar imagen"
              >
                <Text style={{ fontSize: 12, fontWeight: '600', color: ticketColors.ink }}>Cambiar imagen</Text>
              </Pressable>
              <Pressable
                onPress={onRemoveImage}
                disabled={uploading}
                style={[styles.ticketFormInput, { minHeight: 40, justifyContent: 'center', opacity: uploading ? 0.6 : 1 }]}
                accessibilityRole="button"
                accessibilityLabel="Quitar imagen"
              >
                <Text style={{ fontSize: 12, fontWeight: '600', color: ticketColors.ink3 }}>Quitar imagen</Text>
              </Pressable>
            </View>
          </View>
        ) : (
          <Pressable
            onPress={onPickImage}
            disabled={uploading}
            style={[styles.ticketFormInput, styles.railImageDropzone, { opacity: uploading ? 0.6 : 1 }]}
            accessibilityRole="button"
            accessibilityLabel="Agregar imagen"
            testID="catalog.form.image.pick"
          >
            {uploading ? <ActivityIndicator size="small" color={ticketColors.ink2} /> : <Text style={{ fontSize: 20, color: ticketColors.ink3 }}>＋</Text>}
            <View style={{ gap: 2 }}>
              <Text style={{ fontSize: 13, fontWeight: '600', color: ticketColors.ink2 }}>
                {uploading ? 'Subiendo imagen…' : 'Agregá una imagen'}
              </Text>
              <Text style={styles.imageHelper}>JPG o PNG · se ve en catálogo y tienda</Text>
            </View>
          </Pressable>
        )}
        {uploading ? (
          <View style={styles.imageWarnNotice}>
            <Text style={styles.imageWarnNoticeText}>Subiendo…</Text>
          </View>
        ) : null}
      </View>
    );
  }

  if (imageUrl) {
    return (
      <View style={[styles.ticketFormField, { flexDirection: 'row', alignItems: 'center', gap: 12 }]}>
        <View
          style={{
            width: IMAGE_SIZE,
            height: IMAGE_SIZE,
            borderRadius: 10,
            backgroundColor: '#FFFFFF',
            overflow: 'hidden',
          }}
        >
          <Image
            source={{ uri: resolveImageUrl(imageUrl, 'thumb') ?? imageUrl }}
            style={{ width: IMAGE_SIZE, height: IMAGE_SIZE }}
            resizeMode="contain"
          />
        </View>
        <View style={{ flex: 1, gap: 6 }}>
          <Pressable
            onPress={onPickImage}
            disabled={uploading}
            style={({ pressed }) => [
              styles.ticketFormInput,
              { height: 36, alignItems: 'center', justifyContent: 'center', opacity: pressed || uploading ? 0.6 : 1 },
            ]}
          >
            <Text style={{ fontSize: 12, fontWeight: '600', color: ticketColors.ink }}>
              Cambiar imagen
            </Text>
          </Pressable>
          <Pressable
            onPress={onRemoveImage}
            disabled={uploading}
            style={({ pressed }) => [
              styles.ticketFormInput,
              { height: 36, alignItems: 'center', justifyContent: 'center', opacity: pressed || uploading ? 0.6 : 1 },
            ]}
          >
            <Text style={{ fontSize: 12, fontWeight: '600', color: ticketColors.ink3 }}>
              Quitar imagen
            </Text>
          </Pressable>
          <Text style={styles.imageHelper}>Vista previa · imagen lista</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.ticketFormField}>
      <Text style={styles.ticketFormLabel}>Imagen del producto</Text>
      <Pressable
        onPress={onPickImage}
        disabled={uploading}
        style={({ pressed }) => [
          styles.ticketFormInput,
          {
            height: IMAGE_SIZE,
            alignItems: 'center',
            justifyContent: 'center',
            opacity: pressed || uploading ? 0.6 : 1,
            borderStyle: 'dashed',
            borderWidth: 1,
          },
        ]}
      >
        {uploading ? (
          <View style={{ alignItems: 'center', gap: 6 }}>
            <ActivityIndicator size="small" color={ticketColors.ink2} />
            <Text style={{ fontSize: 12, fontWeight: '600', color: ticketColors.ink3 }}>
              Subiendo imagen…
            </Text>
          </View>
        ) : (
          <Text style={{ fontSize: 12, fontWeight: '600', color: ticketColors.ink3 }}>
            Agregá una imagen · JPG o PNG
          </Text>
        )}
      </Pressable>
      <Text style={styles.imageHelper}>
        La imagen se muestra en el catálogo y en tu tienda pública.
      </Text>
      {uploading ? (
        <View style={styles.imageWarnNotice}>
          <Text style={styles.imageWarnNoticeText}>
            Subiendo…
          </Text>
        </View>
      ) : null}
    </View>
  );
}
